(() => {
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = (path) => `${root}${path.replace(/^\//, '')}`;
  const ADD_TO_CART_URL = cartUrl('cart/add.js');
  const CART_URL = cartUrl('cart.js');

  function getAddButtons(form) {
    return Array.from(form.querySelectorAll('[data-cart-add-button]'));
  }

  function setButtonState(button, state) {
    if (!button) return;

    const defaultText =
      button.dataset.defaultText ||
      button.dataset.addText ||
      button.textContent.trim();

    if (!button.dataset.defaultText) {
      button.dataset.defaultText = defaultText;
    }

    const loadingText = button.dataset.loadingText || 'Adding...';

    switch (state) {
      case 'loading':
        button.disabled = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');
        button.textContent = loadingText;
        break;

      case 'success':
        button.disabled = false;
        button.classList.remove('is-loading');
        button.setAttribute('aria-busy', 'false');
        button.textContent = 'Added';
        window.setTimeout(() => {
          if (!button.disabled) button.textContent = button.dataset.defaultText;
        }, 1600);
        break;

      case 'error':
        button.disabled = false;
        button.classList.remove('is-loading');
        button.setAttribute('aria-busy', 'false');
        button.textContent = 'Try again';
        window.setTimeout(() => {
          if (!button.disabled) button.textContent = button.dataset.defaultText;
        }, 2200);
        break;

      default:
        button.disabled = false;
        button.classList.remove('is-loading');
        button.setAttribute('aria-busy', 'false');
        button.textContent = button.dataset.defaultText;
    }
  }

  function setFormState(form, state) {
    getAddButtons(form).forEach((button) => setButtonState(button, state));
    form.classList.toggle('is-loading', state === 'loading');
  }

  function getErrorMessage(errorData) {
    if (!errorData) return 'Unable to add this item to your cart.';
    if (typeof errorData.description === 'string') return errorData.description;
    if (typeof errorData.message === 'string') return errorData.message;
    return 'Unable to add this item to your cart.';
  }

  async function getCart() {
    const response = await fetch(CART_URL, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Unable to refresh cart data.');
    return response.json();
  }

  async function addToCart(form) {
    const response = await fetch(ADD_TO_CART_URL, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(getErrorMessage(data));
    return data;
  }

  async function handleProductFormSubmit(event) {
    const form = event.currentTarget;

    if (form.dataset.cartAddLoading === 'true') {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    form.dataset.cartAddLoading = 'true';
    setFormState(form, 'loading');

    const inlineError = form.querySelector('[data-cart-add-error]');
    if (inlineError) {
      inlineError.hidden = true;
      inlineError.textContent = '';
    }

    // Open immediately, show the loader, and hide the empty cart state.
    if (typeof CartDrawer !== 'undefined') {
      CartDrawer.open(event.submitter || document.activeElement);
      CartDrawer.setLoading(true);
      CartDrawer.setStatus('Adding item to cart.');
    }

    document.dispatchEvent(
      new CustomEvent('cart:adding', { detail: { form } })
    );

    try {
      // This is the only /cart/add.js request for [data-cart-add-form] forms.
      const addedItem = await addToCart(form);
      const cart = await getCart();

      setFormState(form, 'success');

      if (typeof CartDrawer !== 'undefined') {
        CartDrawer.renderFromData(cart);
        CartDrawer.setStatus('Item added to cart.');
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart, addedItem, form },
        })
      );
    } catch (error) {
      console.error('[product-form] Cart add error:', error);

      setFormState(form, 'error');

      if (inlineError) {
        inlineError.textContent = error.message || 'Unable to add this item to your cart.';
        inlineError.hidden = false;
      }

      if (typeof CartDrawer !== 'undefined') {
        CartDrawer.showError(error.message || 'Unable to add this item to your cart.');
        CartDrawer.setStatus('Could not add item to cart.');
      }

      document.dispatchEvent(
        new CustomEvent('cart:error', { detail: { error, form } })
      );
    } finally {
      if (typeof CartDrawer !== 'undefined') {
        CartDrawer.setLoading(false);
      }

      window.setTimeout(() => {
        form.dataset.cartAddLoading = 'false';
      }, 300);
    }
  }

  function initializeProductForms(container = document) {
    container.querySelectorAll('[data-cart-add-form]').forEach((form) => {
      if (form.dataset.cartAddFormLoaded === 'true') return;

      form.dataset.cartAddFormLoaded = 'true';
      form.addEventListener('submit', handleProductFormSubmit);
    });
  }

  function init() {
    initializeProductForms();

    // Re-initialise forms inserted by quick-add modals or section rendering.
    document.addEventListener('cart:form:init', (event) => {
      const container = event.detail?.container ?? document;
      initializeProductForms(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();